import React, { useState, useEffect, useRef } from "react";
import { FiX, FiUpload } from "react-icons/fi";
import toast from "react-hot-toast";

import {
  LOGO_ACCEPT,
  LOGO_HINT,
  logoFileError,
  iconUrlChanged,
  imageFromClipboard,
} from "../../../utils/sponsorLogo";

const SponsorModal = ({ isOpen, onClose, onSave, editingSponsor = null, eventId }) => {
  const isEditing = Boolean(editingSponsor);
  const [name, setName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [iconUrl, setIconUrl] = useState("");
  const [iconFile, setIconFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [useUpload, setUseUpload] = useState(true);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingSponsor) {
      setName(editingSponsor.name || "");
      // The API sends the same link under both names, matching the mobile API.
      setWebsiteUrl(editingSponsor.website_url || editingSponsor.url || "");
      setSortOrder(editingSponsor.sort_order ?? 0);
      setIsActive(editingSponsor.is_active ?? true);
      setIconUrl(editingSponsor.icon_url || "");
      setPreviewUrl(editingSponsor.icon_url || "");
      setIconFile(null);
      setUseUpload(false);
    } else {
      setName("");
      setWebsiteUrl("");
      setSortOrder(0);
      setIsActive(true);
      setIconUrl("");
      setPreviewUrl("");
      setIconFile(null);
      setUseUpload(true);
    }
  }, [editingSponsor, isOpen]);

  if (!isOpen) return null;

  const acceptLogo = (file) => {
    const error = logoFileError(file);
    if (error) {
      toast.error(error);
      return false;
    }

    setIconFile(file);
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(URL.createObjectURL(file));
    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!acceptLogo(file)) e.target.value = "";
  };

  /**
   * Paste an image straight into the modal.
   *
   * A logo is usually already on the clipboard — a screenshot, or an image
   * copied from the sponsor's own site — and saving it to disk first only to
   * pick it back up is a detour. Text pastes are left alone, so pasting a URL
   * into the URL box still behaves like a URL box.
   */
  const handlePaste = (e) => {
    const image = imageFromClipboard(e);
    if (!image) return;

    // Only once an image is actually found, so a text paste is untouched.
    e.preventDefault();
    if (acceptLogo(image)) {
      setUseUpload(true);
      toast.success("Logo pasted from clipboard");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }

    setLoading(true);
    try {
      if (iconFile) {
        const formData = new FormData();
        formData.append("name", name.trim());
        formData.append("website_url", websiteUrl.trim());
        formData.append("sort_order", Number(sortOrder) || 0);
        formData.append("is_active", isActive ? "true" : "false");
        // `logo` is the name the API's responses use; `icon` and `image` are
        // still accepted aliases, but this is the one to send.
        formData.append("logo", iconFile);
        await onSave(formData, editingSponsor?.id, true);
      } else {
        const jsonPayload = {
          name: name.trim(),
          website_url: websiteUrl.trim(),
          sort_order: Number(sortOrder) || 0,
          is_active: Boolean(isActive),
        };
        // Only a URL somebody actually edited. Sending back the absolute one
        // the API just handed us would overwrite the relative path it stores,
        // and the next response would prefix it a second time.
        if (!isEditing || iconUrlChanged(iconUrl, editingSponsor?.icon_url)) {
          jsonPayload.icon_url = iconUrl.trim();
        }
        await onSave(jsonPayload, editingSponsor?.id, false);
      }
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save sponsor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h2 className="text-sm font-semibold text-gray-800">
            {isEditing ? "Edit Event Sponsor" : "Add Event Sponsor"}
          </h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition"
          >
            <FiX size={17} />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} onPaste={handlePaste} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Sponsor Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Acme Crypto Partner"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Website URL</label>
            <input
              type="url"
              placeholder="https://sponsor.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            />
          </div>

          {/* Icon selector toggle */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Sponsor Logo / Icon</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setUseUpload(true)}
                  className={`text-[11px] px-2 py-0.5 rounded transition ${
                    useUpload ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => setUseUpload(false)}
                  className={`text-[11px] px-2 py-0.5 rounded transition ${
                    !useUpload ? "bg-blue-100 text-blue-700 font-medium" : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  Direct URL
                </button>
              </div>
            </div>

            {useUpload ? (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept={LOGO_ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 hover:border-blue-400 p-4 rounded-xl flex flex-col items-center justify-center gap-1.5 transition text-gray-500 hover:text-blue-600 bg-gray-50/50"
                >
                  <FiUpload size={20} />
                  <span className="text-xs font-medium">
                    Click to select an image — or press Ctrl+V to paste one
                  </span>
                  <span className="text-[11px] text-gray-400">{LOGO_HINT}</span>
                </button>
              </div>
            ) : (
              <input
                type="url"
                placeholder="https://example.com/logo.png"
                value={iconUrl}
                onChange={(e) => {
                  setIconUrl(e.target.value);
                  setPreviewUrl(e.target.value);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            )}

            {previewUrl && (
              <div className="mt-3 flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl border border-gray-100">
                <img
                  src={previewUrl}
                  alt="Sponsor preview"
                  className="w-12 h-12 object-contain rounded-lg bg-white border border-gray-200 p-1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="text-xs text-gray-500">
                  <p className="font-medium text-gray-700">Logo Preview</p>
                  <p className="text-[11px] text-gray-400 truncate max-w-xs">{iconFile ? iconFile.name : previewUrl}</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sort Order</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              <p className="text-[11px] text-gray-400 mt-1">Lower numbers appear first</p>
            </div>

            <div className="flex flex-col justify-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-xs font-medium text-gray-700">Active (Visible)</span>
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm shadow-blue-600/20 transition flex items-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isEditing ? "Save Changes" : "Add Sponsor"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SponsorModal;
